import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { invalidateUserSessionCredits } from "../../hooks/useAuth";
import { PartySavedRosterSetupSection } from "../../party/PartySavedRosterSetupSection";
import { PartyTimer, PartyTopBar } from "../../party/PartyChrome";
import { usePartySavedRoster } from "../../party/usePartySavedRoster";
import { PartyMascot } from "../../party/PartyMascot";
import { handlePartyRosterNameEnter } from "../../party/partyNameInputEnter";
import { PartyPlayResumePrompt } from "../../party/PartyPlayResumePrompt";
import { TRIAL_CATEGORY_TOPIC } from "../../party/partyTrialMockData";
import { PartyTrialSetupBanner } from "../../party/PartyTrialSetupBanner";
import PartyShareResultButton from "../../party/share/PartyShareResultButton";
import { usePartyShareModeTitle } from "../../party/share/usePartyShareModeTitle";
import { usePartyPlayTrialMode } from "../../party/usePartyPlayTrialMode";
import { apiClient } from "../../utils/api";
import { trackPartyGameCompleted, trackPartyGameStarted } from "../../utils/mixpanel";
import { topicNamesForAnalytics } from "../../utils/modeCategoryLabels";
import { clearPartyGameCache, readPartyGameCache, writePartyGameCache } from "../../utils/partyGameLocalCache";

type Phase = "setup" | "rules" | "play" | "end";

type SecondsPerTurn = 3 | 5 | 7 | 10;

const CACHE_SHELL = "category";

type CategoryGameCacheData = {
  phase: Phase;
  topic: { name_en?: string; name_ar?: string } | null;
  players: string[];
  turn: number;
  alive: number[];
  eliminatedOrder: number[];
  secondsPerTurn: SecondsPerTurn | null;
};

// --- styled -----------------------------------------------------------------

const SetupRoot = styled(Box)({
  padding: "4px 20px 100px",
});

const PlayRoot = styled(Box)({
  padding: "4px 20px 100px",
});

const EndRoot = styled(Box)({
  padding: "16px 20px 100px",
  textAlign: "center",
  minHeight: "100dvh",
  boxSizing: "border-box",
});

const MascotWrap = styled(Box)({
  marginTop: 8,
  marginBottom: 16,
});

const HintText = styled(Typography)({
  display: "block",
  color: "var(--ink-dim)",
  fontSize: 14,
  marginBottom: 12,
  lineHeight: 1.5,
});

const NameRow = styled(Stack)({
  flexDirection: "row",
  gap: 8,
  marginBottom: 12,
});

const TeamField = styled(TextField)({
  flex: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: "rgba(255,255,255,0.05)",
    color: "var(--ink)",
    "& fieldset": {
      borderColor: "var(--ink-faint)",
      borderWidth: 1.5,
    },
    "&.Mui-focused fieldset": {
      borderColor: "var(--hot)",
    },
  },
});

const PlayerChipsRow = styled(Stack)({
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 6,
  marginBottom: 12,
});

const PlayerChip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "6px 6px 6px 12px",
  borderRadius: 999,
  background: "var(--ink-faint)",
  fontWeight: 700,
  color: "var(--ink)",
});

const ChipRemove = styled(IconButton)({
  width: 28,
  height: 28,
  padding: 0,
  background: "rgba(255,255,255,0.1)",
  color: "var(--ink)",
  "&:hover": { background: "rgba(255,255,255,0.18)" },
});

const TimeOptionButton = styled("button", {
  shouldForwardProp: (p) => p !== "picked",
})<{ picked?: boolean }>(({ picked }) => ({
  flex: 1,
  minWidth: 0,
  padding: "10px 6px",
  borderRadius: 12,
  font: "inherit",
  fontWeight: 700,
  fontSize: 12,
  cursor: "pointer",
  color: "var(--ink)",
  border: picked ? "2px solid var(--hot)" : "1.5px solid var(--ink-faint)",
  background: picked ? "rgba(255,61,120,0.1)" : "rgba(255,255,255,0.04)",
  "&:hover": {
    background: picked ? "rgba(255,61,120,0.14)" : "rgba(255,255,255,0.07)",
  },
}));

const TimeOptionsRow = styled(Stack)({
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 8,
  marginBottom: 16,
});

const StartButton = styled(Button)({
  width: "100%",
});

const CreditErrorText = styled(Typography)({
  display: "block",
  color: "var(--hot)",
  marginTop: 8,
});

const TurnBanner = styled(Box)({
  marginBottom: 12,
  padding: "14px 16px 16px",
  borderRadius: 20,
  textAlign: "center",
  background: "linear-gradient(165deg, color-mix(in oklab, var(--hot) 18%, transparent), rgba(255,255,255,0.04))",
  border: "1.5px solid color-mix(in oklab, var(--hot) 45%, transparent)",
  boxShadow: "0 0 0 2px rgba(255, 61, 120, 0.25), 0 16px 40px -12px rgba(255, 61, 120, 0.28)",
});

const TurnKicker = styled(Box)({
  fontSize: 11,
  letterSpacing: "0.14em",
  color: "var(--ink-dim)",
  marginBottom: 6,
});

const TurnName = styled(Box)({
  fontSize: "clamp(24px, 7vw, 36px)",
  lineHeight: 1.05,
  color: "var(--hot)",
});

const PlayHeadRow = styled(Stack)({
  flexDirection: "row",
  alignItems: "stretch",
  justifyContent: "center",
  gap: 16,
  marginBottom: 12,
});

const TimerWrap = styled(Stack)({
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const CategoryCard = styled(Box)({
  "--card-bg": "var(--paper)",
  color: "var(--paper-ink)",
  padding: 28,
  marginBottom: 18,
  textAlign: "center",
});

const RulesRoot = styled(Box)({
  padding: "4px 20px 100px",
});

const RulesCard = styled(Box)({
  "--card-bg": "var(--paper)",
  color: "var(--paper-ink)",
  padding: "28px 22px 32px",
  marginBottom: 22,
  marginTop: 4,
  textAlign: "center",
  borderRadius: 22,
  boxShadow: "0 20px 48px -20px rgba(0,0,0,0.35)",
});

const RulesHeadingInCard = styled(Typography)({
  fontWeight: 900,
  fontSize: "clamp(22px, 5.8vw, 30px)",
  lineHeight: 1.3,
  marginBottom: 16,
  textAlign: "center",
  color: "var(--paper-ink)",
});

const RulesBodyInCard = styled(Typography)({
  fontSize: "clamp(15px, 3.9vw, 17px)",
  lineHeight: 1.72,
  textAlign: "center",
  color: "color-mix(in oklab, var(--paper-ink) 82%, transparent)",
  maxWidth: 520,
  marginLeft: "auto",
  marginRight: "auto",
});

const PlayActionsStack = styled(Stack)({
  flexDirection: "column",
  gap: 10,
  marginBottom: 20,
});

const RosterSection = styled(Box)({
  marginTop: 4,
});

const RosterTitle = styled(Typography)({
  fontSize: 11,
  letterSpacing: "0.12em",
  color: "var(--ink-dim)",
  fontWeight: 800,
  marginBottom: 8,
});

const RemainingNames = styled(Box)({
  fontSize: "clamp(18px, 4.5vw, 22px)",
  fontWeight: 800,
  lineHeight: 1.35,
  color: "var(--ink)",
});

const EliminatedBlock = styled(Box)({
  marginTop: 16,
  paddingTop: 14,
  borderTop: "1.5px solid var(--ink-faint)",
});

const EliminatedNames = styled(Box)({
  fontSize: 15,
  fontWeight: 600,
  color: "var(--ink-dim)",
  lineHeight: 1.45,
});

const TimerHint = styled(Typography)({
  fontSize: 12,
  color: "var(--ink-dim)",
  marginTop: 10,
  textAlign: "center",
});

const WinnerKicker = styled(Box)({
  fontSize: 12,
  letterSpacing: "0.2em",
  color: "var(--hot)",
  fontWeight: 800,
  textShadow: "0 0 24px rgba(255, 61, 120, 0.35)",
});

const WinnerName = styled(Box)({
  fontSize: "clamp(36px, 11vw, 52px)",
  lineHeight: 1.05,
  marginTop: 10,
  color: "var(--hot)",
  textShadow: "0 0 48px rgba(255, 61, 120, 0.35)",
});

const WinnerSub = styled(Box)({
  fontSize: 15,
  color: "var(--ink-dim)",
  marginTop: 12,
  fontWeight: 700,
});

const EndEliminatedSection = styled(Box)({
  marginTop: 28,
  textAlign: "start",
  maxWidth: 420,
  marginLeft: "auto",
  marginRight: "auto",
});

const EndEliminatedHeading = styled(Typography)({
  fontSize: 11,
  letterSpacing: "0.14em",
  color: "var(--ink-dim)",
  fontWeight: 800,
  marginBottom: 10,
});

const EndEliminatedChips = styled(Stack)({
  flexDirection: "row",
  flexWrap: "wrap",
  gap: 8,
});

const EndEliminatedChip = styled(Box)({
  padding: "8px 14px",
  borderRadius: 999,
  background: "var(--ink-faint)",
  fontWeight: 700,
  fontSize: 14,
  color: "var(--ink)",
});

const HomeButton = styled(Button)({
  width: "100%",
  marginTop: 24,
});

// --- component --------------------------------------------------------------

export default function PartyPlayCategoryGame() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isTrialMode = usePartyPlayTrialMode();
  const savedRoster = usePartySavedRoster();
  const shareModeTitle = usePartyShareModeTitle("category", i18n.language?.startsWith("ar") ? "الفئات" : "Category");
  const [phase, setPhase] = useState<Phase>("setup");
  const [topic, setTopic] = useState<{ name_en?: string; name_ar?: string } | null>(null);
  const [players, setPlayers] = useState<string[]>([]);
  const [newName, setNewName] = useState("");
  const [turn, setTurn] = useState(0);
  const [alive, setAlive] = useState<number[]>([]);
  const [eliminatedOrder, setEliminatedOrder] = useState<number[]>([]);
  const [secondsPerTurn, setSecondsPerTurn] = useState<SecondsPerTurn | null>(5);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [gameStartedAtMs, setGameStartedAtMs] = useState<number | null>(null);
  const completionTrackedRef = useRef(false);

  const [cacheEnvelope] = useState(() => readPartyGameCache<CategoryGameCacheData>(CACHE_SHELL));
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const showResumePrompt = cacheEnvelope != null && !resumeDismissed;

  const playersRef = useRef(players);
  useEffect(() => {
    playersRef.current = players;
  }, [players]);

  const topicLabel = useMemo(() => {
    const ar = topic?.name_ar?.trim();
    const en = topic?.name_en?.trim();
    if (i18n.language?.startsWith("ar")) return ar || en || t("party.playCategoryGame.categoryCardLabel");
    return en || ar || t("party.playCategoryGame.categoryCardLabel");
  }, [i18n.language, t, topic?.name_ar, topic?.name_en]);

  const currentIdx = alive[turn % Math.max(alive.length, 1)] ?? 0;

  const eliminateAtIndex = useCallback((playerIndex: number) => {
    setAlive((prev) => {
      if (!prev.includes(playerIndex)) return prev;
      setEliminatedOrder((o) => (o.includes(playerIndex) ? o : [...o, playerIndex]));
      const nextAlive = prev.filter((i) => i !== playerIndex);
      if (nextAlive.length <= 1) setPhase("end");
      return nextAlive;
    });
    setTurn(0);
  }, []);

  const eliminateCurrent = useCallback(() => {
    eliminateAtIndex(currentIdx);
  }, [currentIdx, eliminateAtIndex]);

  const passCurrent = useCallback(() => {
    setTurn((t0) => t0 + 1);
  }, []);

  useEffect(() => {
    if (phase !== "play" || secondsPerTurn == null) {
      setSecondsLeft(null);
      return;
    }
    const playerForThisTurn = currentIdx;
    const total = secondsPerTurn;
    let left = total;
    setSecondsLeft(left);
    const id = window.setInterval(() => {
      left -= 1;
      setSecondsLeft(left);
      if (left <= 0) {
        window.clearInterval(id);
        eliminateAtIndex(playerForThisTurn);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [phase, secondsPerTurn, currentIdx, eliminateAtIndex]);

  const startMut = useMutation({
    mutationFn: () => apiClient.postCategoryGameStart(),
    onSuccess: (data: { topic?: { name_en?: string; name_ar?: string } }) => {
      const p = playersRef.current;
      savedRoster.recordPlayersBatch(p);
      setTopic(data?.topic ?? null);
      setEliminatedOrder([]);
      setAlive(p.map((_, i) => i));
      setTurn(0);
      setPhase("rules");
      setGameStartedAtMs(Date.now());
      completionTrackedRef.current = false;
      invalidateUserSessionCredits(queryClient);
      trackPartyGameStarted({
        shell_id: "category",
        category_names: topicNamesForAnalytics(data?.topic ?? null),
        number_of_players: p.length,
        trial: false,
        mode_name_en: "Category countdown",
        seconds_per_turn: secondsPerTurn,
      });
    },
  });

  const beginCategoryTrial = useCallback(() => {
    const p = playersRef.current;
    savedRoster.recordPlayersBatch(p);
    setTopic(TRIAL_CATEGORY_TOPIC);
    setEliminatedOrder([]);
    setAlive(p.map((_, i) => i));
    setTurn(0);
    setPhase("rules");
    setGameStartedAtMs(Date.now());
    completionTrackedRef.current = false;
    trackPartyGameStarted({
      shell_id: "category",
      category_names: topicNamesForAnalytics(TRIAL_CATEGORY_TOPIC),
      number_of_players: p.length,
      trial: true,
      mode_name_en: "Category countdown",
      seconds_per_turn: secondsPerTurn,
    });
  }, [savedRoster, secondsPerTurn]);

  const addPlayer = () => {
    const n = newName.trim();
    if (!n) return;
    setPlayers((p) => [...p, n]);
    setNewName("");
    savedRoster.recordPlayerAdded(n);
  };

  const removePlayerAt = (i: number) => setPlayers((p) => p.filter((_, j) => j !== i));

  useEffect(() => {
    if (phase === "end") {
      clearPartyGameCache(CACHE_SHELL);
      return;
    }
    const id = window.setTimeout(() => {
      const payload: CategoryGameCacheData = {
        phase,
        topic,
        players,
        turn,
        alive,
        eliminatedOrder,
        secondsPerTurn,
      };
      writePartyGameCache(CACHE_SHELL, payload);
    }, 400);
    return () => window.clearTimeout(id);
  }, [phase, topic, players, turn, alive, eliminatedOrder, secondsPerTurn]);

  useEffect(() => {
    if (phase !== "end" || gameStartedAtMs == null || completionTrackedRef.current) return;
    completionTrackedRef.current = true;
    trackPartyGameCompleted({
      shell_id: "category",
      trial: isTrialMode,
      mode_name_en: "Category countdown",
      duration_seconds: Math.max(0, Math.round((Date.now() - gameStartedAtMs) / 1000)),
    });
  }, [gameStartedAtMs, isTrialMode, phase]);

  const applyCategoryCache = useCallback((data: CategoryGameCacheData) => {
    const ph =
      data.phase === "play" || data.phase === "setup" || data.phase === "rules" ? data.phase : "setup";
    setPhase(ph);
    setTopic(data.topic ?? null);
    setPlayers(Array.isArray(data.players) ? data.players : []);
    setTurn(typeof data.turn === "number" ? data.turn : 0);
    setAlive(Array.isArray(data.alive) ? data.alive : []);
    setEliminatedOrder(Array.isArray(data.eliminatedOrder) ? data.eliminatedOrder : []);
    const s = data.secondsPerTurn;
    setSecondsPerTurn(s === 3 || s === 5 || s === 7 || s === 10 || s === null ? s : 5);
  }, []);

  const eliminatedNames = eliminatedOrder.map((idx) => players[idx]).filter(Boolean);
  const remainingNames = alive.map((idx) => players[idx]).filter(Boolean);

  if (phase === "setup") {
    return (
      <SetupRoot>
        <PartyTopBar title={t("party.playCategoryGame.setupTitleBar")} onBack={() => navigate("/party/detail/category")} />
        <Stack alignItems="center" sx={{ py: 1.5 }}>
          <PartyMascot shellId="category" size={100} />
        </Stack>
        {isTrialMode && <PartyTrialSetupBanner />}
        <HintText>{t("party.playCategoryGame.setupHint")}</HintText>
        <Typography className="party-mono" sx={{ fontSize: 11, mb: 1, color: "var(--ink-dim)", fontWeight: 800 }}>
          {t("party.playCategoryGame.timeLimitLabel")}
        </Typography>
        <TimeOptionsRow>
          <TimeOptionButton type="button" picked={secondsPerTurn === 3} onClick={() => setSecondsPerTurn(3)}>
            {t("party.playCategoryGame.time3")}
          </TimeOptionButton>
          <TimeOptionButton type="button" picked={secondsPerTurn === 5} onClick={() => setSecondsPerTurn(5)}>
            {t("party.playCategoryGame.time5")}
          </TimeOptionButton>
          <TimeOptionButton type="button" picked={secondsPerTurn === 7} onClick={() => setSecondsPerTurn(7)}>
            {t("party.playCategoryGame.time7")}
          </TimeOptionButton>
          <TimeOptionButton type="button" picked={secondsPerTurn === 10} onClick={() => setSecondsPerTurn(10)}>
            {t("party.playCategoryGame.time10")}
          </TimeOptionButton>
          <Box sx={{ width: "100%" }}>
            <TimeOptionButton type="button" picked={secondsPerTurn === null} onClick={() => setSecondsPerTurn(null)} style={{ width: "100%" }}>
              {t("party.playCategoryGame.timeUntimed")}
            </TimeOptionButton>
          </Box>
        </TimeOptionsRow>
        <NameRow>
          <TeamField
            size="small"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("party.playCategoryGame.playerPlaceholder")}
            onKeyDown={(e) => handlePartyRosterNameEnter(e, addPlayer)}
            hiddenLabel
            InputProps={{ "aria-label": t("party.playCategoryGame.playerPlaceholder") }}
          />
          <IconButton
            type="button"
            onClick={addPlayer}
            aria-label={t("party.playCategoryGame.addPlayerAria")}
            className="party-btn party-btn-paper"
            sx={{ borderRadius: 2, width: 48, height: 48, flexShrink: 0 }}
          >
            <AddIcon />
          </IconButton>
        </NameRow>
        <PlayerChipsRow>
          {players.map((p, i) => (
            <PlayerChip key={`${p}-${i}`}>
              {p}
              <ChipRemove
                type="button"
                size="small"
                aria-label={t("party.playCategoryGame.removePlayerAria")}
                onClick={() => removePlayerAt(i)}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </ChipRemove>
            </PlayerChip>
          ))}
        </PlayerChipsRow>
        <PartySavedRosterSetupSection
          api={savedRoster}
          variant="players"
          currentPlayers={players}
          onQuickAddPlayer={(name) => {
            setPlayers((p) => [...p, name]);
            savedRoster.recordPlayerAdded(name);
          }}
        />
        {showResumePrompt && cacheEnvelope ? (
          <PartyPlayResumePrompt
            savedAtIso={cacheEnvelope.savedAt}
            onContinue={() => {
              applyCategoryCache(cacheEnvelope.data);
              setResumeDismissed(true);
            }}
            onNewGame={() => {
              clearPartyGameCache(CACHE_SHELL);
              setResumeDismissed(true);
            }}
          />
        ) : (
          <StartButton
            type="button"
            disabled={players.length < 2 || (!isTrialMode && startMut.isPending)}
            className="party-btn party-btn-hot"
            sx={{ opacity: players.length >= 2 ? 1 : 0.5 }}
            onClick={() => (isTrialMode ? beginCategoryTrial() : startMut.mutate())}
          >
            {isTrialMode ? t("party.trial.continueSampleCta") : t("party.playCategoryGame.fetchTopicCta")}
          </StartButton>
        )}
        {!isTrialMode && (startMut.error as { response?: { status?: number } })?.response?.status === 402 && (
          <CreditErrorText>{t("party.playCategoryGame.creditInsufficient")}</CreditErrorText>
        )}
      </SetupRoot>
    );
  }

  if (phase === "rules" && topic) {
    return (
      <RulesRoot>
        <PartyTopBar title={t("party.playCategoryGame.rulesTitleBar")} onBack={() => navigate("/party/detail/category")} />
        <RulesCard className="party-kan-card party-anim-fade-scale-in">
          <RulesHeadingInCard className="party-display">{t("party.playCategoryGame.rulesHeading")}</RulesHeadingInCard>
          <RulesBodyInCard>{t("party.playCategoryGame.rulesBody")}</RulesBodyInCard>
        </RulesCard>
        <Stack direction="column" gap={1.25}>
          <Button type="button" className="party-btn party-btn-hot" fullWidth onClick={() => setPhase("play")}>
            {t("party.playCategoryGame.rulesStartCta")}
          </Button>
          <Button
            type="button"
            className="party-btn party-btn-paper"
            fullWidth
            onClick={() => {
              setPhase("setup");
              setTopic(null);
              setAlive([]);
              setEliminatedOrder([]);
              setTurn(0);
            }}
          >
            {t("party.playCategoryGame.rulesBackCta")}
          </Button>
        </Stack>
      </RulesRoot>
    );
  }

  if (phase === "play" && alive.length > 0) {
    const showTimer = secondsPerTurn != null && secondsLeft != null && secondsPerTurn > 0;
    return (
      <PlayRoot>
        <PartyTopBar title={topicLabel} onBack={() => setPhase("rules")} />
        <PlayHeadRow>
          <TurnBanner className="party-anim-fade-scale-in" sx={{ flex: 1, minWidth: 0 }}>
            <TurnKicker className="party-mono">{t("party.playCategoryGame.turnKicker")}</TurnKicker>
            <TurnName className="party-display">{t("party.playCategoryGame.turnName", { name: players[currentIdx] })}</TurnName>
          </TurnBanner>
          {showTimer && (
            <TimerWrap>
              <PartyTimer seconds={secondsLeft} total={secondsPerTurn} />
            </TimerWrap>
          )}
        </PlayHeadRow>

        <CategoryCard className="party-kan-card party-anim-fade-scale-in">
          <Typography className="party-mono" sx={{ fontSize: 12, letterSpacing: "0.1em", opacity: 0.88, mb: 1.25 }} component="div">
            {t("party.playCategoryGame.categoryCardLabel")}
          </Typography>
          <Typography
            className="party-display"
            sx={{ fontSize: "clamp(34px, 11vw, 56px)", mt: 0.5, lineHeight: 1.12, fontWeight: 900 }}
            component="div"
          >
            {topicLabel}
          </Typography>
        </CategoryCard>

        <PlayActionsStack>
          <Button type="button" className="party-btn party-btn-hot" fullWidth onClick={passCurrent}>
            {t("party.playCategoryGame.passCorrect")}
          </Button>
          <Button type="button" className="party-btn party-btn-paper" fullWidth onClick={eliminateCurrent}>
            {t("party.playCategoryGame.failEliminate")}
          </Button>
        </PlayActionsStack>

        <RosterSection>
          <RosterTitle className="party-mono">{t("party.playCategoryGame.remainingTitle")}</RosterTitle>
          <RemainingNames className="party-display">{remainingNames.join("، ")}</RemainingNames>

          <EliminatedBlock>
            <RosterTitle className="party-mono">{t("party.playCategoryGame.eliminatedTitle")}</RosterTitle>
            <EliminatedNames>
              {eliminatedNames.length ? eliminatedNames.join("، ") : t("party.playCategoryGame.noneEliminatedYet")}
            </EliminatedNames>
          </EliminatedBlock>
        </RosterSection>

        {secondsPerTurn != null && <TimerHint>{t("party.playCategoryGame.timerHint")}</TimerHint>}
      </PlayRoot>
    );
  }

  if (phase === "end") {
    const winnerIdx = alive[0];
    const winner = winnerIdx != null ? players[winnerIdx] : t("party.playCategoryGame.noWinner");
    return (
      <EndRoot>
        <PartyTopBar title={t("party.playCategoryGame.endTitleBar")} onBack={() => navigate("/party/detail/category")} />
        <MascotWrap>
          <PartyMascot shellId="category" size={120} />
        </MascotWrap>
        <WinnerKicker className="party-mono party-anim-fade-scale-in">{t("party.playCategoryGame.resultKicker")}</WinnerKicker>
        <WinnerName className="party-display party-anim-fade-scale-in">{winner}</WinnerName>
        <WinnerSub className="party-mono">{t("party.playCategoryGame.resultChampionSub")}</WinnerSub>
        <Typography className="party-display" sx={{ fontSize: 20, mt: 2, color: "var(--ink)" }} component="div">
          {t("party.playCategoryGame.resultTopic", { topic: topicLabel })}
        </Typography>

        {eliminatedNames.length > 0 && (
          <EndEliminatedSection>
            <EndEliminatedHeading className="party-mono">{t("party.playCategoryGame.eliminatedSectionTitle")}</EndEliminatedHeading>
            <EndEliminatedChips>
              {eliminatedNames.map((name, ei) => (
                <EndEliminatedChip key={`${name}-${ei}`}>{name}</EndEliminatedChip>
              ))}
            </EndEliminatedChips>
          </EndEliminatedSection>
        )}

        <PartyShareResultButton
          className="party-btn party-btn-paper"
          sx={{ width: "100%", maxWidth: 420, mt: 2 }}
          model={{
            shellId: "category",
            modeTitle: shareModeTitle,
            resultTitle: t("party.playCategoryGame.endTitleBar"),
            hero: winner,
            sub: t("party.playCategoryGame.resultTopic", { topic: topicLabel }),
            meta: eliminatedNames.length
              ? [t("party.playCategoryGame.eliminatedSectionTitle"), eliminatedNames.join("، ")]
              : [t("party.playCategoryGame.eliminatedSectionTitle"), t("party.playCategoryGame.noneEliminatedYet")],
          }}
        />

        <HomeButton type="button" className="party-btn party-btn-hot" onClick={() => navigate("/")}>
          {t("party.playCategoryGame.home")}
        </HomeButton>
      </EndRoot>
    );
  }

  return null;
}
